import type { ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { CredentialingStatus, PublicCredentialingStatus } from '../../types';
import { IntakePill } from '../shell/IntakePill';
import { footnoteSx, wf } from '../shell/tokens';

import { CREDENTIALING_STATUS_LABELS } from './credentialingStatus';
import { CredentialingStatusSelect } from './CredentialingStatusPanel';

/** The design's table header: 11px/700 tracked uppercase on a tinted row. */
const headerCellSx = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: '#6b6b78',
  backgroundColor: '#fafafd',
} as const;

export interface CredentialingMatrixCell {
  providerId: string;
  providerLabel: ReactNode;
  /** Absent means no record exists yet — "Not started", never a fabricated row. */
  record?: PublicCredentialingStatus;
}

export interface CredentialingMatrixGroup {
  key: string;
  payerLabel: ReactNode;
  /** Why this payer is listed, e.g. the intake form answer it came from. */
  caption?: string;
  /** Blocks editing and explains why, e.g. an unresolvable payer directory id. */
  notice?: string;
  cells: CredentialingMatrixCell[];
}

function credentialingStatusTone(status: CredentialingStatus) {
  return status === 'complete' ? 'green' : status === 'in_progress' ? 'indigo' : 'slate';
}

/**
 * One row per (provider × payer), with the payers the practice asked to be
 * credentialed for driving the list rather than the records that happen to
 * exist. A pair with no record reads "Not started"; setting a status is what
 * creates the record.
 *
 * Purely presentational: the caller owns the roster read, the payer-identity
 * join and the mutations.
 */
export function CredentialingRosterMatrix({
  groups,
  trailingGroups = [],
  trailingTitle,
  trailingBlurb,
  onChange,
  saving = false,
}: {
  groups: CredentialingMatrixGroup[];
  /** Records whose payer is not on the intake roster, kept visible below. */
  trailingGroups?: CredentialingMatrixGroup[];
  trailingTitle?: string;
  trailingBlurb?: string;
  onChange?: (group: CredentialingMatrixGroup, cell: CredentialingMatrixCell, status: CredentialingStatus) => void;
  saving?: boolean;
}) {
  const renderGroup = (group: CredentialingMatrixGroup) =>
    group.cells.map((cell, index) => (
      <TableRow key={`${group.key}:${cell.providerId}`}>
        {index === 0 && (
          <TableCell
            rowSpan={group.cells.length}
            sx={{ overflowWrap: 'anywhere', verticalAlign: 'top', minWidth: 200 }}
          >
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: wf.ink }}>
              {group.payerLabel}
            </Typography>
            {group.caption && (
              <Typography sx={{ ...footnoteSx, mt: '2px' }}>{group.caption}</Typography>
            )}
            {group.notice && (
              <Typography sx={{ fontSize: '12px', color: wf.red, mt: '4px' }}>
                {group.notice}
              </Typography>
            )}
          </TableCell>
        )}
        <TableCell sx={{ overflowWrap: 'anywhere' }}>{cell.providerLabel}</TableCell>
        <TableCell>
          {onChange && !group.notice ? (
            <CredentialingStatusSelect
              label={`Status for ${cell.providerId} / ${group.key}`}
              value={cell.record?.status ?? 'not_started'}
              disabled={saving}
              onChange={status => onChange(group, cell, status)}
            />
          ) : (
            <IntakePill
              label={CREDENTIALING_STATUS_LABELS[cell.record?.status ?? 'not_started']}
              tone={credentialingStatusTone(cell.record?.status ?? 'not_started')}
            />
          )}
        </TableCell>
      </TableRow>
    ));
  return (
    <TableContainer
      sx={{
        border: '1px solid #e2e2ea',
        borderRadius: '10px',
        '& .MuiTableCell-root': { borderColor: '#eeeef4' },
      }}
    >
      <Table aria-label="Credentialing statuses">
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>Payer</TableCell>
            <TableCell sx={headerCellSx}>Provider</TableCell>
            <TableCell sx={headerCellSx}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map(renderGroup)}
          {trailingGroups.length > 0 && (
            <TableRow>
              <TableCell colSpan={3} sx={{ backgroundColor: wf.surfaceAlt }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: wf.ink }}>
                  {trailingTitle ?? 'Not on the intake roster'}
                </Typography>
                {trailingBlurb && (
                  <Typography sx={{ ...footnoteSx, mt: '2px' }}>{trailingBlurb}</Typography>
                )}
              </TableCell>
            </TableRow>
          )}
          {trailingGroups.map(renderGroup)}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
